import { Key, useCallback, useEffect, useMemo, useRef } from "react";

import { MCDANIEL_NAME } from "constants/settings.constants";

import { useProjectContext, useProjectDispatch } from "../../../projects/context";
import {
  useSelectedProject,
  useSelectedProjectPermissions
} from "../../../projects/hooks";
import { CheckedKeysT } from "../../components/layer-managers/project-layer-tree/MemoizedTree";
import { ProjectTreeNodeT } from "../index";
import {
  SHAPEFILE_SOURCE,
  TREE_NODE_TYPE
} from "../project-layer-tree/useProjectLayerTree.types";

export type UseManageLayerTreeReturnT = {
  isManageMode: boolean;
  checkedKeys: string[];
  tree: ProjectTreeNodeT[] | null;
  handleEnterManageMode: () => void;
  handleExitManageMode: () => void;
  handleOnCheck: (checkedKeys: Key[] | CheckedKeysT) => string[];
};

type UseManageLayerTreeParamsT = {
  existingTree: ProjectTreeNodeT[];
};

/**
 * Hook for managing the state of shapefile management mode in the project layer tree.
 * Follows the same pattern as useSearchLayerTree for consistency.
 *
 * In manage mode:
 * - Only shapefile layers and folders can be checked (others are disabled)
 * - Provides separate checked keys state from regular tree operations
 * - Allows bulk deletion of selected shapefiles and folders
 */
export const useManageLayerTree = (
  params: UseManageLayerTreeParamsT
): UseManageLayerTreeReturnT => {
  const { existingTree } = params;
  const projectContext = useProjectContext();
  const projectDispatch = useProjectDispatch();

  const { selectedProject: project } = useSelectedProject();
  const selectedProjectPermissions = useSelectedProjectPermissions();

  const checkedKeys = projectContext.manageMode?.checkedKeys || [];
  const isManageMode = projectContext.manageMode?.isEnabled || false;

  const canEnterManageMode = useMemo(() => {
    if (!project) return false;
    if (project.projectType === MCDANIEL_NAME) return false;
    if (!selectedProjectPermissions?.canEditProjectItems) return false;
    return true;
  }, [project, selectedProjectPermissions]);

  // Modified tree with disableCheckbox for non-shapefile nodes
  const tree = useMemo(() => {
    if (!isManageMode || !existingTree) return null;

    const modifyTreeForManageMode = (nodes: ProjectTreeNodeT[]): ProjectTreeNodeT[] => {
      return nodes.map((node) => {
        const isShapefileNode =
          (node.type === TREE_NODE_TYPE.LAYER || node.type === TREE_NODE_TYPE.FOLDER) &&
          (node.source === SHAPEFILE_SOURCE.PROJECT ||
            node.source === SHAPEFILE_SOURCE.ORG ||
            node.source === SHAPEFILE_SOURCE.MCDAN);

        return {
          ...node,
          disableCheckbox: !isShapefileNode,
          children: node.children ? modifyTreeForManageMode(node.children) : undefined
        };
      });
    };

    return modifyTreeForManageMode(existingTree);
  }, [isManageMode, existingTree]);

  const handleEnterManageMode = useCallback(() => {
    if (!canEnterManageMode) {
      return;
    }

    projectDispatch({
      payload: {
        manageMode: {
          isEnabled: true,
          checkedKeys: []
        }
      }
    });
  }, [projectDispatch, canEnterManageMode]);

  const handleExitManageMode = useCallback(() => {
    projectDispatch({
      payload: {
        manageMode: {
          isEnabled: false,
          checkedKeys: []
        }
      }
    });
  }, [projectDispatch]);

  const prevProjectIdRef = useRef(projectContext.selectedProjectId);

  // Clear manage mode when project changes
  useEffect(() => {
    const currentProjectId = projectContext.selectedProjectId;
    const prevProjectId = prevProjectIdRef.current;

    if (prevProjectId !== currentProjectId && isManageMode) {
      handleExitManageMode();
    }

    prevProjectIdRef.current = currentProjectId;
  }, [projectContext.selectedProjectId, isManageMode, handleExitManageMode]);

  const handleOnCheck = useCallback(
    (checkedKeysParam: Key[] | CheckedKeysT): string[] => {
      const newCheckedKeys = Array.isArray(checkedKeysParam)
        ? (checkedKeysParam as string[])
        : (checkedKeysParam as CheckedKeysT).checked;

      projectDispatch({
        payload: {
          manageMode: {
            isEnabled: true,
            checkedKeys: newCheckedKeys
          }
        }
      });

      return newCheckedKeys;
    },
    [projectDispatch]
  );

  return {
    isManageMode,
    checkedKeys,
    tree,
    handleEnterManageMode,
    handleExitManageMode,
    handleOnCheck
  };
};
